import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import Benefits from '../components/Benefits';
import CarOverview from '../components/CarOverview';
import CarView, { ButtonContainer } from '../components/CarView';
import Features from '../components/Features';
import InspectionReport from '../components/InspectionReport';
import LoanCalculator from '../components/LoanCalculator';
import Questionnaire from '../components/Questionnaire';
import { formatMoney } from '../components/SearchFilter';
import ServiceHistory from '../components/ServiceHistory';
import Services from '../components/Services';
import Specifications from '../components/Specifications';
import Summary from '../components/Summary';
import Loading from '../components/ui/Loading';
import SecSeperator from '../components/ui/SecSeperator';
import Layout from '../layout/Layout';

import { Typography } from 'antd';
import { ArrowDown, ArrowUp } from 'phosphor-react';
import CarIssue from '../components/CarIssue';
import CarListByType from '../components/CarListByType';
import ChooseAuto24 from '../components/ChooseAuto24';
import { COUNTRY_TOP_LEVEL_DOMAIN, IMAGE_URL } from '../constants';
import { ICar } from '../interface/car';
import { getDetail } from '../redux/actions/carListingAction';
import { ICombineReducerType } from '../redux/reducers';
import { addImageFallback } from '../utils/Helper/ImagePlaceholder';
import { SetMeta, VehicleStructuredData } from '../utils/Helper/jsHelpers';
import { getTopLevelDomain } from '../utils/translation';
import { CAR_LIST_TYPE_ENUM } from './Home';
import LoanPaymentSimulator from './LoanPaymentSimulator';
import Notfound from './Notfound';

const { Paragraph } = Typography;

const DetailPage = () => {
    const [scrolled, setScrolled] = useState(false);
    const [isFooterVisible, setIsFooterVisible] = useState(false);
    const [activeNavClick, setActiveNavClick] = useState('');
    let { id }: any = useParams();
    const dispatch = useDispatch();
    const [car, setCar] = useState<ICar>();
    const [isError, setIsError] = useState(false);
    const { similarCars, totalSimilarCars, filtersUsed } = useSelector(
        (state: ICombineReducerType) => state.carListing
    );
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        getDetail(dispatch, id)
            .then((res) => setCar(res.car))
            .catch((err) => setIsError(true));
        window.scrollTo(0, 0);
    }, [id]);

    const { t, i18n } = useTranslation();

    const topLevelDomain = getTopLevelDomain();

    const handleToggle = () => {
        setExpanded(!expanded);
    };

    const detectScroll = () => {
        if (window.scrollY >= 60) {
            setScrolled(true);
        } else {
            setScrolled(false);
        }
    };
    const metaTitle = car
        ? t('carDetailPageTitle', {
              ns: 'meta',
              make: car?.name,
              price: formatMoney(car?.price)
          })
        : 'Loading...';
    const metaDes = t('carDetailPageDesc', {
        ns: 'meta',
        make: car?.name,
        PRIX: formatMoney(car?.price)
    });
    useEffect(() => {
        window.addEventListener('scroll', detectScroll);
        return () => window.removeEventListener('scroll', detectScroll);
    }, []);

    const filteredDetails = car?.details?.filter(
        (item: any) => item.lang === i18n.language
    )[0];

    // Build the query string based on filtersUsed and car state
    const buildQueryParams = () => {
        const params = new URLSearchParams();

        // Define the mapping for each filter key to its property path
        const filterMappings: Record<string, string> = {
            brand: 'brand.slug',
            model: 'model.slug',
            transmission: 'transmission',
            fuelType: 'fuelType',
            bodyType: 'bodyType.slug',
            minMileage: 'mileage',
            minPrice: 'price',
            color: 'exteriorColor.slug',
            modelYear: 'modelYear'
        };

        if (Array.isArray(filtersUsed)) {
            filtersUsed.forEach((filter) => {
                let value;

                // Get the property path from the mapping
                const propertyPath = filterMappings[filter];
                if (propertyPath) {
                    // Access nested properties based on the mapping
                    const properties = propertyPath.split('.');
                    value = properties.reduce(
                        (obj: any, prop) => obj?.[prop],
                        car
                    );
                } else {
                    value = (car as any)?.[filter];
                }

                if (value !== undefined && value !== null) {
                    params.append(filter, String(value));
                }
            });
        }

        return params.toString() ? `?${params.toString()}` : '';
    };

    const queryParams = buildQueryParams();

    if (isError) {
        return <Notfound />;
    }

    return (
        <>
            <div className="detail-page">
                <SetMeta title={metaTitle} desc={metaDes} />
                <VehicleStructuredData car={car ?? ({} as ICar)} />
                <Layout
                    showWhiteNavbar
                    ShowdetailsNav
                    setIsFooterVisible={setIsFooterVisible}
                    setActiveNavClick={setActiveNavClick}
                    newCarFlag={car?.isNewCar}
                >
                    {!car ? (
                        <div style={{ height: '20vh' }}>
                            <Loading />
                        </div>
                    ) : (
                        <>
                            <CarView car={car} newCarFlag={car?.isNewCar} />
                            <Services />
                            {car?.details?.length &&
                            filteredDetails?.description &&
                            filteredDetails.description !== '.' ? (
                                <div className="discover padding">
                                    <SecSeperator className="center" />
                                    <h2 className="title">{t('discover')}</h2>
                                    {filteredDetails?.description.split(' ')
                                        .length < 100 ? (
                                        <p className="discover-content">
                                            {filteredDetails?.description}
                                        </p>
                                    ) : (
                                        <div>
                                            <Paragraph
                                                ellipsis={
                                                    !expanded
                                                        ? {
                                                              rows: 4,
                                                              expandable: true,
                                                              symbol: ' '
                                                          }
                                                        : false
                                                }
                                                style={{ textAlign: 'justify' }}
                                                className={
                                                    expanded
                                                        ? 'expanded'
                                                        : 'collapsed'
                                                }
                                            >
                                                {filteredDetails?.description}
                                            </Paragraph>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <button
                                                    onClick={handleToggle}
                                                    className="showDesc"
                                                >
                                                    {expanded
                                                        ? 'Show Less'
                                                        : 'Show More'}
                                                    {expanded ? (
                                                        <ArrowUp
                                                            size={16}
                                                            color="#FFF"
                                                            weight="bold"
                                                        />
                                                    ) : (
                                                        <ArrowDown
                                                            size={16}
                                                            color="#FFF"
                                                            weight="bold"
                                                        />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : null}
                            <CarOverview activeNavClick={activeNavClick} />
                            <Summary activeNavClick={activeNavClick} />
                            <Specifications activeNavClick={activeNavClick} />
                            <Features activeNavClick={activeNavClick} />
                            {car.technicalVisitCertificate === null &&
                            car.keys === null ? null : (
                                <div className="additional padding discover">
                                    <SecSeperator className="center" />
                                    <h2 className="title">
                                        {t('additionalPhotos')}
                                    </h2>

                                    <div className="additional-photos">
                                        {car.technicalVisitCertificate && (
                                            <div className="additional-content">
                                                <p>{t('technicalVisit')}</p>
                                                <div className="image-container">
                                                    <img
                                                        src={`${IMAGE_URL}${car.technicalVisitCertificate}`}
                                                        alt="Technical visiti certificates"
                                                        onError={
                                                            addImageFallback
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        {car.keys && (
                                            <div className="additional-content">
                                                <p>{t('keysPhotos')}</p>
                                                <div className="image-container">
                                                    <img
                                                        src={`${IMAGE_URL}${car.keys}`}
                                                        alt="Keys"
                                                        onError={
                                                            addImageFallback
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            <ServiceHistory activeNavClick={activeNavClick} />
                            {car.inspectionReport ? (
                                <InspectionReport {...car.inspectionReport} />
                            ) : null}
                            <CarIssue />
                            <ChooseAuto24 />
                            <Benefits />
                            {topLevelDomain === COUNTRY_TOP_LEVEL_DOMAIN.ma ? (
                                <div className="loan-simulator-wrapper">
                                    <LoanPaymentSimulator
                                        carPrice={car?.price}
                                    />
                                </div>
                            ) : (
                                <div className="loan-simulator-wrapper">
                                    <LoanCalculator
                                        carPrice={car?.price}
                                        carId={car?._id}
                                    />
                                </div>
                            )}
                            <CarListByType
                                title="similarResults"
                                subtitle="hereyoucanseedifferentsimilarcars"
                                carData={
                                    similarCars.length > 6
                                        ? similarCars.slice(0, 6)
                                        : similarCars
                                }
                                total={totalSimilarCars}
                                type={CAR_LIST_TYPE_ENUM.custom}
                                queryParams={queryParams}
                            />
                            <Questionnaire slug={car?.slug} />
                            {scrolled && !isFooterVisible && (
                                <ButtonContainer />
                            )}
                        </>
                    )}
                </Layout>
            </div>
        </>
    );
};

export default DetailPage;
